import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {ControlContainer, NgForm, NgModel} from '@angular/forms';
import {QuestionDTO} from '../../../model/questionDTO';

@Component({
  selector: 'app-question',
  styleUrls: ['./question.component.scss'],
  templateUrl: './question.component.html',
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})

export class QuestionComponent implements OnInit {

  @Input() question;
  @Input() answers;
  @Input() questions: Array<QuestionDTO>;
  @Input() model;
  @Input() showError;
  @Input() reportTypeCode;
  @Input() reportName;
  @Output() recalculate = new EventEmitter<void>();
  @Output() save = new EventEmitter<string>();
  @Output() unsaved = new EventEmitter<string>();

  timeout;
  timeoutInterval = 1000; // 1s

  id; // for brevity

  postDecimalOptions = [
    {
      value: 'YEARLY',
      label: 'Per jaar'
    },
    {
      value: 'QUARTERLY',
      label: 'Per kwartaal'
    },
    {
      value: 'MONTHLY',
      label: 'Per maand'
    },
    {
      value: 'QUAD_WEEKLY',
      label: 'Per vier weken'
    },
    {
      value: 'WEEKLY',
      label: 'Per week'
    },
    {
      value: 'DAILY',
      label: 'Per dag'
    }
  ];

  static calculateMonthly(value, period) {
    if (!value) return 0;
    let monthlyValue;
    switch (period) {
      case 'YEARLY':
        monthlyValue = value / 12;
        break;
      case 'QUARTERLY':
        monthlyValue = value / 3;
        break;
      case 'QUAD_WEEKLY':
        monthlyValue = value * 13 / 12;
        break;
      case 'WEEKLY':
        monthlyValue = value * 52 / 12;
        break;
      case 'DAILY':
        monthlyValue = value * 30.44;
        break;
      case 'MONTHLY':
      default:
        monthlyValue = value;
        break;
    }
    return monthlyValue;
  }

  static numberToEuro (number) {
    return '€ ' + Number(Math.round(number * 100) / 100)
      .toLocaleString('nl-NL', {minimumFractionDigits: 2}) + ' per maand.';
  }

  selectLabelFormatter =  (choice: any) => choice.value.replace('{REPORT}', this.reportName);

  ngOnInit() {
    this.id = this.question.identification;
    this.initQuestion(this.question, this.id);
  }

  private initQuestion(question, id) {
    if (this.model[id] == null) {
      if (question.type === 'LIST') {
        this.model[id] = [];
        this.model[id].push({});
        this.model[id].push({});
      }
      if (question.type === 'MULTI_SELECT') {
        this.model[id] = {};
      }
      if (question.type === 'POST_DECIMAL' && this.reportTypeCode === 'BB') {
        this.model[id] = {};
        this.model[id]['period'] = 'MONTHLY';
      }
    }
  }

  addRow() {
    this.model[this.id].push({});
  }

  onChange() {
    this.handleSave();
  }

  calculate() {
    this.recalculate.emit();
    this.handleSave();
  }

  private handleSave() {
    this.unsaved.emit(this.id);
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    this.timeout = setTimeout(() => {
      clearTimeout(this.timeout);
      this.deleteDependentAnswers(this.question.identification, false);
      this.save.emit(this.id);
    }, this.timeoutInterval);
  }

  private deleteDependentAnswers(identification: string, isSubquestion: boolean) {
    const question = this.questions.filter(q => q.identification === identification)[0];
    question?.dependentQuestionDependencies?.forEach(dependency => {
      const {requiredOption, ownerIdentification, questionIdentification} = dependency;
      this.deleteDependentAnswers(ownerIdentification, true);
      // tslint:disable-next-line:triple-equals
      if ((this.model[questionIdentification] != requiredOption || isSubquestion) && this.notEmpty(this.model[ownerIdentification])) {
        delete this.model[ownerIdentification];
        // give empty tables 2 empty rows
        this.initQuestion(
          this.questions.filter(q => q.identification === ownerIdentification)[0],
          ownerIdentification
        );
        this.save.emit(ownerIdentification);
      }
    });
  }

  private notEmpty = (answer: any): boolean => {
    if (answer instanceof Array) {
      let empty = false;
      for (let i = 0; i < answer.length; i++) {
        for (const field in answer[i]) {
          if (Object.prototype.hasOwnProperty.call(answer[i], field) && this.notEmpty(field)) {
            empty = true;
          }
        }
      }
      return empty;
    } else {
      return answer !== null && answer !== '' && answer !== undefined;
    }
  }

  getMonthlyValue(value, period) {
    if (value == null) { return; }
    const monthlyValue = QuestionComponent.calculateMonthly(value, period);
    if (monthlyValue) {
      return QuestionComponent.numberToEuro(monthlyValue);
    }
  }

  readOnly() {
    return this.reportTypeCode === 'BB' &&
      (this.id === 'nPzLw5YdLqFlrxLRmEsC31UkPihkPA0D' || this.id === 'ynkIpeoc7icFAI0yB9gzKJ8DTRfcpAnx');
  }

  required() {
    return this.question.required;
  }

  valid(name: NgModel) {
    return this.showError && !name.valid;
  }

  requiredSubquestion(row) {
    if (row === {}) return false;
    for (const field in row) {
      if (Object.prototype.hasOwnProperty.call(row, field)) {
        if (row[field] !== null && row[field] !== '') {
          return true;
        }
      }
    }
    return false;
  }

  shouldShow(question: QuestionDTO) {
    if (!question?.questionDependency?.questionIdentification) return true;
    if (question?.type === 'FILE') return false;

    const dependentQuestion = this.questions.filter(q => q.identification === question?.questionDependency?.questionIdentification)[0];
    const answer = this.model[question?.questionDependency?.questionIdentification];
    const requiredOption = question.questionDependency.requiredOption;

    // tslint:disable-next-line:triple-equals
    return requiredOption == answer || (dependentQuestion?.type === 'MULTI_SELECT' && answer[requiredOption]);
  }
}
